<template>
	<div
		class="offer-container"
		:class="{
			'rounded-bottom': release.isLastOfGroup,
		}"
	>
		<slot name="banner"></slot>
		<div
			class="offer offer-container-upcoming"
			:class="{
				'has-banner': release.isFirst,
				'has-border': release.isInGroup,
				'has-bottom-border': release.isLastOfGroup,
			}"
		>
			<div class="offer__content">
				<div class="content-wrapper content-wrapper--left">
					<div class="picture-wrapper">
						<ProviderIcon
							v-if="upcomingProviderIcon"
							:iconUrl="upcomingProviderIcon"
							:providerShortName="props.release.offer.package?.shortName"
							:lazy="false"
						/>

						<div v-else class="jw-icon-container">
							<img height="24" src="appassets/img/jw-icon.png" alt="JustWatch Logo" />
						</div>
					</div>

					<div class="offer__presentation" @click.prevent.stop="toggleBuyBoxInfoModal(true)">
						<div class="offer__presentation__icons">
							<p class="offer__presentation__icons__subtitles">CC</p>
							<p v-if="ageRating" class="offer__presentation__icons__age">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="15"
									height="12"
									viewBox="0 0 15 12"
									fill="none"
								>
									<circle cx="6" cy="6" r="5.5" stroke="#5AA152" />
									<path
										d="M3.5 5.5L7 8.5L14 1"
										stroke="white"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
								{{ ageRating }}
							</p>
						</div>
						<p class="offer__presentation__info">
							<span v-if="runtimeMins">{{ runtimeMins }}</span>
						</p>
					</div>
				</div>

				<div class="content-wrapper content-wrapper--right">
					<div class="offer__label">
						<p v-if="hasfreeTrial" class="offer__label__free-trial">{{ freeTrialText }}</p>
						<p v-else-if="offerLabel" class="offer__label__text">{{ offerLabel }}</p>
						<p v-if="retailPrice" class="offer__label__price">
							<span>{{ retailPrice }}</span>
						</p>
						<!-- This will still make sure the space is still taken up (it's transparent) -->
						<p v-else class="offer__label__price transparent">retail price</p>
					</div>
					<button
						class="offer__cta"
						:class="{ 'offer__cta--highlight': !isTitleInWatchlist }"
						@click="$emit('onNotifyMe')"
					>
						<FontAwesomeIcon
							:icon="isTitleInWatchlist ? jwCheckIcon : faBell"
							size="lg"
							class="play-icon"
							:class="{ primary: isTitleInWatchlist }"
						/>
						<span class="offer__cta__text">{{ t('WEBAPP_BUTTON_NOTIFY_ME') }}</span>
					</button>
				</div>
			</div>
			<BuyBoxInfoModal
				:displayBuyBoxInfoModal="displayBuyBoxInfoModal"
				:offer="release.offer"
				:title="title"
				@close="toggleBuyBoxInfoModal($event)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { MonetizationType } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UpcomingReleaseBuybox } from '@/features/buybox/types/types.ts';
import { jwCheckIcon } from '@/plugins/global-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
const BuyBoxInfoModal = defineAsyncComponent(() => import('@/features/buybox/components/list/BuyBoxInfoModal.vue'));

const props = defineProps<{
	release: UpcomingReleaseBuybox;
	title: TitleDetail;
	isTitleInWatchlist?: boolean;
}>();

defineEmits<{
	(e: 'onNotifyMe'): void;
}>();

const { t } = useI18n();
const displayBuyBoxInfoModal = ref<boolean>(false);

const providerUnknown = computed(
	() => !props.release.offer.package || ['imd', 'tmd'].includes(props.release.offer.package.shortName)
);

const upcomingProviderIcon = computed(() => {
	if (providerUnknown.value) {
		const icon =
			props.release.offer.releaseType === 'THEATRICAL' ? 'JW_cinema_icon_wide' : 'JW_streaming_icon_wide';
		return `https://www.justwatch.com/${ASSETS_DIR}/img/icons/${icon}.{format}`;
	}
	return props.release.offer.package?.iconWide || props.release.offer.package?.icon;
});

const retailPrice = computed(() => {
	const subscriptionPrice = selectedPackagePlan.value?.retailPrice;
	if (!subscriptionPrice) return null;

	return t(hasfreeTrial.value ? 'WEBAPP_THEN_X_PER_MONTH' : 'WEBAPP_X_PER_MONTH', {
		price: subscriptionPrice,
	});
});

const hasfreeTrial = computed(() => {
	return !!selectedPackagePlan.value?.isTrial;
});

const selectedPackagePlan = computed(() => {
	if (!props.release.offer.package?.planOffers && props.release.offer.package?.planOffers.length! > 0) return null;
	return props.release.offer.package?.planOffers[0];
});

const freeTrialText = computed(() => {
	if (!hasfreeTrial.value || !selectedPackagePlan.value?.durationDays) {
		return null;
	}

	const days = selectedPackagePlan.value?.durationDays;
	if (days === 1) return t('WEBAPP_1_DAY_FREE');
	return t('WEBAPP_X_DAYS_FREE', { days: days });
});

const offerLabel = computed(() => {
	if (props.release.offer.package?.planOffers.length)
		return offerLabelFromMonetizationType(MonetizationType.Flatrate);
	if (props.release.offer.releaseType === 'THEATRICAL')
		return offerLabelFromMonetizationType(MonetizationType.Cinema);
	return monetizationType.value ? offerLabelFromMonetizationType(monetizationType.value) : '';
});

const monetizationType = computed(() => {
	return props.release.offer.package?.monetizationTypes[0];
});

const offerLabelFromMonetizationType = (monetizationType: MonetizationType) => {
	const monetizationTypeMapping = {
		ADS: t('WEBAPP_FREE_WITH_ADS'),
		BUY: t('WEBAPP_BUY'),
		CINEMA: t('WEBAPP_CINEMA'),
		FAST: t('WEBAPP_FREE_TV'),
		FLATRATE: t('WEBAPP_SUBSCRIPTION'),
		FLATRATE_AND_BUY: t('WEBAPP_BUY'),
		FREE: t('WEBAPP_FREE'),
		RENT: t('WEBAPP_RENT'),
	};
	return monetizationTypeMapping[monetizationType];
};

const ageRating = computed(() => (props.title.__typename !== 'Season' ? props.title.content.ageCertification : null));

const runtimeMins = computed(() => {
	return props.title.content?.runtime ? `${props.title.content.runtime}min` : null;
});

function toggleBuyBoxInfoModal(display: boolean) {
	displayBuyBoxInfoModal.value = display;
}
</script>

<style lang="scss" scoped>
.content-wrapper {
	flex: 1;
	display: flex;
	justify-content: space-between;

	@media (max-width: 1024px) {
		flex-direction: column;
		justify-content: flex-start;
		width: 50%;
	}

	&--left {
		align-items: flex-start;

		@media (max-width: 1345px) {
			justify-content: flex-start;
		}
	}

	&--right {
		flex: 1;
		align-items: flex-end;
		max-width: 50%;

		@media (max-width: 1024px) {
			flex: 0 1 auto;
			max-width: none;
		}
	}
}

.offer {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 48px;
	width: 100%;
	height: 104px;
	padding: 16px;
	background-color: inherit;

	@media (max-width: 1024px) {
		flex-direction: column;
		height: auto;
		gap: 16px;
	}

	&__content {
		display: flex;
		gap: 16px;
		align-items: flex-start;
		justify-content: space-between;
		width: 100%;

		@media (max-width: 1024px) {
			gap: 8px;
		}

		&__countdown {
			position: relative;
			background-color: #332800;
			color: #fbc500;
			padding: 4px 8px;
			border-radius: 250px;
			margin-top: -4px;
			z-index: 9;
			min-width: fit-content;
			-webkit-line-clamp: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	&__label {
		@media (max-width: 1024px) {
			margin-bottom: 10px;
			text-align: end;
		}

		&__text {
			margin: 0;
			color: #fff;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin-bottom: 4px;
		}

		&__free-trial {
			margin: 0;
			color: #ffe894;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin-bottom: 4px;
		}

		&__price {
			margin: 0;
			color: #c6c8cd;
			font-size: 14px;
			font-weight: 400;
			line-height: 24px;
			letter-spacing: 0.21px;
		}
	}

	&__presentation {
		display: flex;
		flex-direction: column;
		width: 100%;

		@media (max-width: 1024px) {
			justify-content: flex-end;
			gap: 4px;
			margin-top: 26px;
		}

		&__info {
			margin: 0;
			color: #c6c8cd;
			font-size: 14px;
			line-height: 24px;
			letter-spacing: 0.21px;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			text-overflow: ellipsis;

			@media (max-width: 1024px) {
				margin-top: 0;
				margin-bottom: 0;
				-webkit-line-clamp: 1;
			}

			&:hover {
				cursor: pointer;
			}
		}

		&__icons {
			display: flex;
			align-items: center;
			gap: 4px;
			flex-grow: 1;
			margin-bottom: 4px;
			margin-top: 1px;
			max-height: 16px;

			@media (max-width: 1024px) {
				flex-grow: 0;
				justify-content: flex-start;
			}

			&__subtitles,
			&__quality {
				color: #c6c8cd;
				font-size: 11px;
				letter-spacing: 0.18px;
				border-radius: 2px;
				border: 1px solid #c6c8cd;
				padding: 0 2px;
				margin: 0;
				line-height: 16px;
			}

			&__quality {
				color: #fbc500;
				border: 1px solid #fbc500;
			}

			&__age,
			&__dolby {
				display: flex;
				align-items: center;
				gap: 1px;
				color: #fff;
				font-size: 11px;
				line-height: 16px;
				letter-spacing: 0.18px;
				margin: 0;
				white-space: nowrap;
				text-overflow: ellipsis;
			}
		}
	}

	&__cta {
		margin-inline-start: 8px;

		height: 40px;
		width: fit-content;

		min-width: 166px;

		padding: 8px 16px;
		border-radius: 8px;
		border: none;
		background: #303740;
		color: var(--jw-grey-200);
		font-size: 16px;
		font-weight: 900;
		letter-spacing: 0.24px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;

		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;

		&:hover {
			background: #303740cb;
		}
		@media (max-width: 1100px) {
			min-width: 110px;
		}

		@media (max-width: 1050px) {
			min-width: 166px;
		}

		@media (max-width: 1024px) {
			width: fit-content;
			min-width: 166px;
			padding: 0 16px;
			margin-top: 26px;
			flex: 0 1 auto;
			margin-inline-start: 0;
		}

		&__text {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}
}

.picture-wrapper {
	min-width: 120px;
	min-height: 40px;
	margin-bottom: auto;
	margin-inline-end: 48px;

	@media (max-width: 1345px) {
		min-width: 80px;
	}

	@media (max-width: 1024px) {
		margin-inline-end: 16px;
		margin-bottom: 10px;
	}
}

.play-icon {
	margin-inline-end: 8px;
	height: 16px !important;
	&.primary {
		color: var(--jw-yellow-500);
	}
}

.offer-container,
.offer-container-upcoming {
	overflow: hidden;
}

.offer-container-upcoming {
	&.is-first {
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
	}
}

.jw-icon-container {
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 100%;
	border-radius: 8px;
	padding: 4px;
	background-color: #060d18;
	height: 32px;
}

.transparent {
	color: transparent;
}

.has-border {
	border-left: 2px solid var(--jw-support-success-low);
	border-right: 2px solid var(--jw-support-success-low);
	&.has-bottom-border {
		border-bottom: 2px solid var(--jw-support-success-low);
		border-radius: 0px 0px 8px 8px;
	}
}
.rounded-bottom {
	border-radius: 0px 0px 8px 8px;
}
</style>
